import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';

const SIDEBAR_STORAGE_KEY = 'sidebar-status';

type SidebarStatus = 'opened' | 'closed';

/**
 * The collapsed state the user left the rail in, from localStorage, or the
 * default for the screen size when there is none.
 *
 * Read after mount, never as the initial state. The app is server-rendered and
 * hydrated: the server cannot see localStorage and always renders the rail
 * expanded, and React 17's `hydrate` does not correct class names that differ
 * from the server's HTML. Reading this during the first render left a user with
 * `closed` stored looking at an expanded rail that React believed collapsed, so
 * anything mounted after hydration (the Setup section) drew collapsed inside it
 * and the first click of the toggle only reconciled the two.
 */
export const readStoredCollapsed = (): boolean => {
  if (typeof window === 'undefined') return false; // Default to expanded on server

  // Storage that is blocked (a private window, site data turned off) throws on
  // access, which must not take the page down: it reads as nothing stored.
  let cachedSidebarStatus: SidebarStatus | null = null;
  try {
    cachedSidebarStatus = localStorage.getItem(
      SIDEBAR_STORAGE_KEY
    ) as SidebarStatus | null;
  } catch {
    cachedSidebarStatus = null;
  }
  if (cachedSidebarStatus === 'opened') return false; // opened means not collapsed
  if (cachedSidebarStatus === 'closed') return true; // closed means collapsed

  // Desktop defaults to expanded (false = not collapsed)
  // Mobile defaults to collapsed (true = collapsed)
  const isMobile = window.innerWidth < 768;
  return isMobile;
};

/** Remember the user's choice, if storage lets us. */
export const writeStoredCollapsed = (collapsed: boolean): void => {
  const status: SidebarStatus = collapsed ? 'closed' : 'opened';
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, status);
  } catch {
    // Blocked storage: the choice holds for this page and is not remembered.
  }
};

interface SidebarStoreValue {
  /**
   * What the rail actually renders as. The user's stored preference OR a
   * transient force, so anything reading this to size the rail keeps working
   * without knowing which of the two is responsible.
   */
  isCollapsed: boolean;
  /** The stored preference on its own, ignoring any force. */
  preferCollapsed: boolean;
  /** Whether something is currently forcing the rail shut. */
  isForcedCollapsed: boolean;
  /**
   * True for the two frames in which the stored preference is applied after
   * mount. The rail and the page beside it drop their transitions while it is
   * set, so the rail is simply in the state the user left it. Without it, a user
   * who keeps the rail closed watched it slide shut on every page load. A force
   * (the chat panel opening) still animates.
   */
  suppressTransition: boolean;
  /** A user action. Writes through to localStorage and clears any force. */
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  /**
   * Collapse the rail without touching the stored preference, for when the
   * window no longer has room for it beside a panel.
   *
   * This exists because `setCollapsed` persists, and reusing it here would
   * quietly rewrite a preference the user set by hand: open a chat panel once
   * on a laptop and the rail would stay collapsed on the 27" monitor forever
   * after. A force is remembered by nobody and lifts the moment the room comes
   * back.
   */
  setForcedCollapsed: (forced: boolean) => void;
}

const SidebarContext = createContext<SidebarStoreValue | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
  // Expanded on the first render, on the server and in the browser alike, so
  // hydration matches; the stored preference applies right after mount.
  const [preferCollapsed, setPreferCollapsed] = useState(false);
  const [isForcedCollapsed, setIsForcedCollapsed] = useState(false);

  const [suppressTransition, setSuppressTransition] = useState(false);

  useEffect(() => {
    if (!readStoredCollapsed()) return;
    setSuppressTransition(true);
    setPreferCollapsed(true);
  }, []);

  // Two frames, not one: the first is where the snapped layout is committed,
  // and the transitions have to come back only after it has painted.
  useEffect(() => {
    if (!suppressTransition) return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setSuppressTransition(false));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [suppressTransition]);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setPreferCollapsed(collapsed);
    // An explicit action outranks a force: a user who expands the rail while
    // something is holding it shut means it, and the rail must actually move.
    // The thing that forced it is expected to re-force on its next resize if it
    // still has cause to, which is what keeps this from fighting the user.
    setIsForcedCollapsed(false);
    // A click animates, even inside a snap that has not finished.
    setSuppressTransition(false);
    writeStoredCollapsed(collapsed);
  }, []);

  // Reads the rendered state, not the preference, so the first click always does
  // what the rail looks like it should do. Forced shut, this expands.
  const toggleCollapsed = useCallback(() => {
    setCollapsed(!(preferCollapsed || isForcedCollapsed));
  }, [preferCollapsed, isForcedCollapsed, setCollapsed]);

  // Stable across renders on purpose: the shell calls this from an effect keyed
  // on the fit result, and an identity that changed every render would re-run
  // that effect on every render instead of when the fit actually changes.
  const setForcedCollapsed = useCallback((forced: boolean) => {
    setIsForcedCollapsed(forced);
  }, []);

  const value = useMemo<SidebarStoreValue>(
    () => ({
      isCollapsed: preferCollapsed || isForcedCollapsed,
      preferCollapsed,
      isForcedCollapsed,
      suppressTransition,
      setCollapsed,
      toggleCollapsed,
      setForcedCollapsed,
    }),
    [
      preferCollapsed,
      isForcedCollapsed,
      suppressTransition,
      setCollapsed,
      toggleCollapsed,
      setForcedCollapsed,
    ]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSidebarStore(): SidebarStoreValue {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarStore must be used within SidebarProvider');
  }
  return context;
}
